import { buildOptions, getEndPoints } from "@/utils/api-call";
import { makeCall, makeDownload } from "@/utils/request";
import { downloadFile } from "@/utils/download-file";
import * as consts from "./constants";
import * as storeUtils from "@/utils/store-handler";

export const state = {
  incentive_templates: {
    data: [],
    meta: {
      pagination: {
        count: 0,
        current_page: 1,
        per_page: 30,
        total: 0,
        total_pages: 0
      }
    }
  },
  incentive_files: {
    data: [],
    meta: {
      pagination: {
        count: 0,
        current_page: 1,
        per_page: 30,
        total: 0,
        total_pages: 0
      }
    }
  }
};

export const getters = {
  [consts.GETTERS_INCENTIVE_TEMPLATES]: state => {
    return state.incentive_templates;
  },
  [consts.GETTERS_INCENTIVE_FILES]: state => {
    return state.incentive_files;
  }
};

export const actions = {
  [consts.ACTION_GET_INCENTIVE_TEMPLATES]: ({ commit }, options) => {
    const apiEndpointUrl = getEndPoints().incentive_processing.getTemplates.replace(
      ":programId",
      options.programId
    );
    makeCall(
      apiEndpointUrl,
      buildOptions("GET", { params: options.params }),
      commit
    ).then(
      response => {
        commit(consts.MUTATION_SET_INCENTIVE_TEMPLATES, response);

        if (options.callback) {
          options.callback();
        }
      },
      error => {
        console.log("Error retrieving incentive templates", error);
        options.errorHandling(storeUtils.handleErrorMessage(error));
        commit(consts.MUTATION_SET_INCENTIVE_TEMPLATES, {
          data: [],
          meta: {
            pagination: {
              count: 0,
              current_page: 1,
              per_page: 30,
              total: 0,
              total_pages: 0
            }
          }
        });
      }
    );
  },
  [consts.ACTION_DOWNLOAD_INCENTIVE_TEMPLATE]: ({ commit }, options) => {
    const downloadTemplateUrl = getEndPoints().incentive_processing.downloadTemplate.replace(
      ":activityId",
      options.activityId
    );
    makeDownload(
      downloadTemplateUrl,
      buildOptions("GET", { responseType: "stream" }, {"Accept": "text/csv"}),
      commit
    ).then(
      response => {
        const contentDisposition = response.headers['content-disposition'];
        const matches = contentDisposition.match(/filename="([^"]*)"/);
        const filename = matches && matches.length > 1 ? matches[1] : 'activity_unknown.csv';

        downloadFile(response.data, filename);
      },
      error => {
        console.log("Error downloading incentive template", error);
        options.errorHandling(storeUtils.handleErrorMessage(error));
      }
    );
  },
  [consts.ACTION_UPLOAD_INCENTIVE_FILE]: ({ commit }, options) => {
    const uploadFileUrl = getEndPoints().incentive_processing.uploadIncentiveFile.replace(
      ":clientId",
      options.clientId
    );

    const formData = new FormData();
    formData.append('files', options.file, options.file.name);

    const headers = {
      'Content-Type': 'multipart/form-data',
    };

    makeCall(
      uploadFileUrl,
      buildOptions("POST", formData, headers),
      commit
    ).then(
      response => {
        if (options.callback) {
          options.callback(response);
        }
      },
      error => {
        console.log("Error uploading incentive file", error);
        options.errorHandling(storeUtils.handleErrorMessage(error));
      }
    );
  },
  [consts.ACTION_PROCESS_INCENTIVE_FILE]: ({ commit }, options) => {
    const processFileUrl = getEndPoints().incentive_processing.processIncentiveFile.replace(
      ":id",
      options.id
    ).replace(
      ":mode",
      options.mode
    );

    makeCall(
      processFileUrl,
      buildOptions("PUT"),
      commit
    ).then(
      response => {
        if (options.callback) {
          options.callback();
        }
      },
      error => {
        console.log("Error processing incentive file", error);
        options.errorHandling(storeUtils.handleErrorMessage(error));
      }
    );
  },
  [consts.ACTION_GET_INCENTIVE_FILES]: ({ commit }, options) => {
    const apiEndpointUrl = getEndPoints().incentive_processing.getIncentiveFiles.replace(
      ":clientId",
      options.clientId
    );
    makeCall(
      apiEndpointUrl,
      buildOptions("GET", { params: options.params }),
      commit
    ).then(
      response => {
        commit(consts.MUTATION_SET_INCENTIVE_FILES, response);

        if (options.callback) {
          options.callback();
        }
      },
      error => {
        console.log("Error retrieving incentive files", error);
        options.errorHandling(storeUtils.handleErrorMessage(error));
        commit(consts.MUTATION_SET_INCENTIVE_FILES, {
          data: [],
          meta: {
            pagination: {
              count: 0,
              current_page: 1,
              per_page: 30,
              total: 0,
              total_pages: 0
            }
          }
        });
      }
    );
  },
  [consts.ACTION_DOWNLOAD_INCENTIVE_FILE]: ({ commit }, options) => {
    const downloadFileUrl = getEndPoints().incentive_processing.downloadIncentiveFile.replace(
      ":id",
      options.id
    );
    makeDownload(
      downloadFileUrl,
      buildOptions("GET", { responseType: "stream" }, {"Accept": "text/csv"}),
      commit
    ).then(
      response => {
        const contentDisposition = response.headers['content-disposition'];
        const matches = contentDisposition.match(/filename="([^"]*)"/);
        const filename = matches && matches.length > 1 ? matches[1] : 'incentive_file.csv';

        downloadFile(response.data, filename);
      },
      error => {
        console.log("Error downloading incentive file", error);
        options.errorHandling(storeUtils.handleErrorMessage(error));
      }
    );
  },
  [consts.ACTION_DOWNLOAD_INCENTIVE_PROCESSING_REPORT]: ({ commit }, options) => {
    const downloadReportUrl = getEndPoints().incentive_processing.downloadIncentiveReport.replace(
      ":id",
      options.id
    );
    makeDownload(
      downloadReportUrl,
      buildOptions("GET", { responseType: "stream" }, {"Accept": "text/csv"}),
      commit
    ).then(
      response => {
        const contentDisposition = response.headers['content-disposition'];
        const matches = contentDisposition.match(/filename="([^"]*)"/);
        const filename = matches && matches.length > 1 ? matches[1] : 'incentive_report.csv';

        downloadFile(response.data, filename);
      },
      error => {
        console.log("Error downloading incentive processing report", error);
        options.errorHandling(storeUtils.handleErrorMessage(error));
      }
    );
  },
};

export const mutations = {
  [consts.MUTATION_SET_INCENTIVE_TEMPLATES]: (state, response) => {
    state.incentive_templates = response;
  },
  [consts.MUTATION_SET_INCENTIVE_FILES]: (state, response) => {
    state.incentive_files = response;
  }
};

export default {
  actions,
  state,
  getters,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/incentive_processing/index.js